import React, { useContext, useEffect, useState, useCallback } from "react";
import api from "../utils/axios";
import { AuthContext } from "../context/AuthContext";
import { Box, Heading, Flex, Button, IconButton, useDisclosure, useBreakpointValue } from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import ErrorMessage from "../components/onlinemenu/ErrorMessage";
import LoadingSpinner from "../components/onlinemenu/LoadingSpinner";
import LinkTable from "../components/links/LinkTable";
import EditModal from "../components/links/EditModal";
import DeactivateModal from "../components/links/DeactivateModal";

const LinksPage = () => {
    const { currLocation } = useContext(AuthContext);
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isDeactivateOpen,
        onOpen: onDeactivateOpen,
        onClose: onDeactivateClose,
    } = useDisclosure();
    const [editData, setEditData] = useState(null);
    const [deactivateData, setDeactivateData] = useState(null);
    const isMobile = useBreakpointValue({ base: true, md: false });

    const fetchLinks = useCallback(async () => {
        if (!currLocation) return;
        try {
            setLoading(true);
            const response = await api.get(`links/location/${currLocation}`);
            setLinks(response.data);
            setError(null);
        } catch (err) {
            setError("Failed to load links.");
        } finally {
            setLoading(false);
        }
    }, [currLocation]);

    useEffect(() => {
        if (!currLocation) return;
        fetchLinks();
    }, [currLocation, fetchLinks]);

    const openEditModal = useCallback((data) => {
        setEditData(data);
        onOpen();
    }, [onOpen]);

    const openDeactivateModal = useCallback((data) => {
        setDeactivateData(data);
        onDeactivateOpen();
    }, [onDeactivateOpen]);

    const handleSave = async (formData) => {
        try {
            const editingId = formData.id !== undefined && formData.id !== 0 ? formData.id : 0;
            const method = editingId ? "put" : "post";
            const apiEndpoint = editingId
                ? `links/${editingId}/location/${currLocation}`
                : `links/location/${currLocation}`;
            // Type auto-generates the URL from metadata for everything except
            // Custom - metadata support isn't built yet, so it's always null.
            const body = { name: formData.name, url: formData.url, type: Number(formData.type), order: formData.order, metadata: null };

            const response = await api[method](apiEndpoint, body);

            if (editingId) {
                setLinks((prev) => prev.map((link) => (link.id === editingId ? { ...link, ...body, ...response.data } : link)));
            } else {
                const savedLink = { isActive: true, metadata: null, ...body, ...response.data };
                setLinks((prev) => [...prev, savedLink]);
                // Self-heal if the create response didn't echo back an id.
                if (!savedLink.id) fetchLinks();
            }

            setEditData(null);
            onClose();
        } catch (err) {
            console.error("Failed to save link:", err);
        }
    };

    const handleDeactivate = async () => {
        try {
            if (!deactivateData) return;
            const response = await api.put(`links/deactivate/${deactivateData.id}`);
            const isActive = response.data?.isActive ?? !deactivateData.isActive;
            setLinks((prev) => prev.map((link) => (link.id === deactivateData.id ? { ...link, isActive } : link)));

            setDeactivateData(null);
            onDeactivateClose();
        } catch (err) {
            console.error("Failed to update active state:", err);
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return <ErrorMessage message={error} />;
    }

    return (
        <Box
            as="main"
            pt={{ base: "24px", md: "20px" }}
            pl={{ base: "0px", md: "240px" }}
            pr={{ base: "0px", md: "30px" }}
            pb="20px"
            minH="100vh"
        >
            <Flex justify="space-between" align="center" mb={6} px={4} pt={4}>
                <Heading color="brand.700" size="lg">Poveznice</Heading>
                {isMobile ? (
                    <IconButton
                        icon={<FaPlus />}
                        aria-label="Dodaj poveznicu"
                        bg="brand.600"
                        color="white"
                        borderRadius="full"
                        onClick={() => openEditModal({ name: "", url: "", type: 0, order: links.length })}
                        _hover={{ bg: "brand.700" }}
                    />
                ) : (
                    <Button
                        leftIcon={<FaPlus />}
                        bg="brand.600"
                        color="white"
                        borderRadius="full"
                        onClick={() => openEditModal({ name: "", url: "", type: 0, order: links.length })}
                        _hover={{ bg: "brand.700" }}
                    >
                        Dodaj poveznicu
                    </Button>
                )}
            </Flex>

            <Box
                bg="white"
                borderRadius="xl"
                border="1px solid"
                borderColor="gray.100"
                boxShadow="card"
                p={3}
                mx={4}
            >
                <LinkTable links={links} onEdit={openEditModal} onDeactivate={openDeactivateModal} />
            </Box>

            <EditModal
                isOpen={isOpen}
                onClose={onClose}
                onSave={handleSave}
                editData={editData}
            />

            <DeactivateModal
                isOpen={isDeactivateOpen}
                onClose={onDeactivateClose}
                onConfirm={handleDeactivate}
                data={deactivateData}
            />
        </Box>
    );
};

export default LinksPage;
